import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { logger } from '@/utils/logger';
import {
  useManufacturingOrder,
  useWorkOrders,
  useReleaseMO,
  useStartWorkOrder,
  useCompleteWorkOrder,
} from '@/hooks/useMES';
// Note: useUpdateMOStatus removed as it was unused
import {
  MO_STATUS_LABELS,
  MO_STATUS_COLORS,
  PRIORITY_LABELS,
  PRIORITY_COLORS,
} from '@/types/mes';
import ManufacturingFlowView from './ManufacturingFlowView';
import WorkOrderDialog from './WorkOrderDialog';

const MODetailPage = () => {
  const { moId } = useParams<{ moId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('mes');

  const [selectedWOId, setSelectedWOId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data: mo, isLoading: moLoading, error: moError } = useManufacturingOrder(moId || '');
  const { data: workOrders = [], isLoading: woLoading } = useWorkOrders(moId || '');

  const releaseMO = useReleaseMO();
  const startWO = useStartWorkOrder();
  const completeWO = useCompleteWorkOrder();

  const selectedWO = workOrders.find((wo) => wo.id === selectedWOId) || null;

  const handleWorkOrderClick = useCallback((woId: string) => {
    setSelectedWOId(woId);
    setDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setSelectedWOId(null);
  }, []);

  const handleRelease = async () => {
    if (!moId) return;
    try {
      await releaseMO.mutateAsync(moId);
    } catch (err) {
      logger.error('Failed to release MO:', err);
    }
  };

  const handleStartWO = async (woId: string, operatorId: string) => {
    try {
      await startWO.mutateAsync({ woId, operatorId });
    } catch (err) {
      logger.error('Failed to start WO:', err);
    }
  };

  const handleCompleteWO = async (woId: string, completedQty: number, scrappedQty: number) => {
    try {
      await completeWO.mutateAsync({ woId, completedQty, scrappedQty });
      handleCloseDialog();
    } catch (err) {
      logger.error('Failed to complete WO:', err);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  if (moLoading || woLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (moError || !mo) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg">
        {t('error.moNotFound', 'Manufacturing order not found')}
      </div>
    );
  }

  const completionPercent = mo.planned_qty > 0
    ? Math.round((mo.completed_qty / mo.planned_qty) * 100)
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/app/mes/orders')}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {mo.mo_number}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {mo.product_name} ({mo.product_code}) - Rev {mo.product_revision}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${MO_STATUS_COLORS[mo.status]}`}>
            {MO_STATUS_LABELS[mo.status]}
          </span>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${PRIORITY_COLORS[mo.priority]}`}>
            {PRIORITY_LABELS[mo.priority]}
          </span>
          {mo.status === 'planned' && (
            <button
              onClick={handleRelease}
              disabled={releaseMO.isLoading}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {releaseMO.isLoading ? t('actions.releasing', 'Releasing...') : t('actions.release', 'Release for Production')}
            </button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Quantity Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
            {t('summary.quantity', 'Quantity')}
          </h3>
          <div className="flex items-end justify-between">
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {mo.completed_qty}
                <span className="text-lg text-gray-400"> / {mo.planned_qty}</span>
              </p>
              {mo.scrapped_qty > 0 && (
                <p className="text-sm text-red-500">{mo.scrapped_qty} scrapped</p>
              )}
            </div>
            <div className="text-right">
              <p className="text-lg font-semibold text-blue-600 dark:text-blue-400">
                {completionPercent}%
              </p>
            </div>
          </div>
          <div className="mt-2 w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-500 transition-all"
              style={{ width: `${Math.min(completionPercent, 100)}%` }}
            />
          </div>
        </div>

        {/* Work Orders Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
            {t('summary.workOrders', 'Work Orders')}
          </h3>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {workOrders.length}
          </p>
          <div className="mt-2 flex gap-2 text-sm">
            <span className="text-green-600">
              {workOrders.filter(wo => wo.status === 'completed').length} done
            </span>
            <span className="text-yellow-600">
              {workOrders.filter(wo => wo.status === 'in_progress').length} active
            </span>
          </div>
        </div>

        {/* Schedule Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
            {t('summary.schedule', 'Schedule')}
          </h3>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">{t('scheduled', 'Scheduled')}:</span>
              <span className="text-gray-900 dark:text-white">
                {formatDate(mo.scheduled_start)}
              </span>
            </div>
            {mo.actual_start && (
              <div className="flex justify-between">
                <span className="text-gray-500">{t('actualStart', 'Started')}:</span>
                <span className="text-gray-900 dark:text-white">
                  {formatDate(mo.actual_start)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Notes Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
            {t('summary.notes', 'Notes')}
          </h3>
          <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-3">
            {mo.notes || t('noNotes', 'No notes')}
          </p>
        </div>
      </div>

      {/* Work Order Flow View */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('flowView.title', 'Production Flow')}
          <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
            {t('flowView.clickToEdit', '(Click a node to view details)')}
          </span>
        </h2>
        {workOrders.length > 0 ? (
          <ManufacturingFlowView
            workOrders={workOrders}
            onWorkOrderClick={handleWorkOrderClick}
          />
        ) : (
          <div className="h-64 flex items-center justify-center text-gray-500 dark:text-gray-400">
            {mo.status === 'draft' || mo.status === 'planned'
              ? t('flowView.notReleased', 'Release the MO to generate work orders')
              : t('flowView.noWorkOrders', 'No work orders found')}
          </div>
        )}
      </div>

      {/* Work Order Dialog */}
      <WorkOrderDialog
        workOrder={selectedWO}
        isOpen={dialogOpen}
        onClose={handleCloseDialog}
        onStartWorkOrder={handleStartWO}
        onCompleteWorkOrder={handleCompleteWO}
      />
    </div>
  );
};

export default MODetailPage;
